import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { BrandFont } from "@/components/BrandFont";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  MessageSquareText, PhoneCall, CalendarDays, Sparkles, Mail, Phone as PhoneIcon,
  UserPlus, LogIn, Waves, CreditCard, Link2, FileText, Download, ExternalLink, BellRing,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = (iso) => { try { return new Date(`${iso}T12:00:00`).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" }); } catch { return iso; } };

const RequestAccessDialog = ({ slug, community, open, onClose }) => {
  const [form, setForm] = useState({ name: "", phone: "", unit: "", email: "", note: "" });
  const [sending, setSending] = useState(false);
  const submit = async () => {
    if (!form.name.trim() || !form.phone.trim()) { toast.error("Your name and mobile number are required"); return; }
    setSending(true);
    try {
      const r = await axios.post(`${API}/public/site/${slug}/request-access`, form);
      toast.success(r.data.message || "Request sent!");
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't send that — try again"); }
    finally { setSending(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="request-access-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Request access</DialogTitle>
          <DialogDescription>Live at {community}? The community team will verify and text you once you&apos;re in.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="ra-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name *" className="rounded-xl border border-sand-200 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
            <input data-testid="ra-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Mobile number *" className="rounded-xl border border-sand-200 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="ra-unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="Unit / address" className="rounded-xl border border-sand-200 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
            <input data-testid="ra-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email (optional)" className="rounded-xl border border-sand-200 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
          </div>
          <textarea data-testid="ra-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} placeholder="Anything the team should know? (optional)" className="w-full rounded-xl border border-sand-200 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30 resize-y" />
        </div>
        <DialogFooter>
          <button onClick={onClose} className="rounded-full px-5 py-2.5 font-semibold text-ink-700 hover:bg-sand-100 transition-colors">Cancel</button>
          <button data-testid="ra-submit" disabled={sending} onClick={submit} className="rounded-full bg-redline-500 text-white font-semibold px-6 py-2.5 hover:bg-redline-600 transition-colors disabled:opacity-60">{sending ? "Sending…" : "Send request"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function CommunitySite() {
  const { slug } = useParams();
  const [site, setSite] = useState(null);
  const [missing, setMissing] = useState(false);
  const [raOpen, setRaOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/site/${slug}`).then((r) => setSite(r.data)).catch(() => setMissing(true));
  }, [slug]);

  if (missing) {
    return (
      <div className="min-h-screen bg-[#1a1614] text-white flex flex-col items-center justify-center gap-3 p-8 text-center font-body">
        <Waves className="h-10 w-10 text-redline-500" />
        <h1 className="font-heading text-2xl font-extrabold">This community page isn&apos;t live yet</h1>
        <p className="text-sand-400 text-sm max-w-sm">Check the address, or ask your community manager to publish their page on Ask Redline.</p>
        <Link to="/" className="mt-2 rounded-full bg-redline-500 hover:bg-redline-600 transition-colors px-6 py-3 font-bold text-sm">What&apos;s Ask Redline?</Link>
      </div>
    );
  }
  if (!site) {
    return <div className="min-h-screen bg-[#f7f4f0] flex items-center justify-center"><div className="h-7 w-7 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" /></div>;
  }

  const num = site.concierge_number;
  // Community branding: accent color drives buttons/highlights; "" = Redline default.
  const brand = site.brand_color || "#D32F2F";
  const brandLight = `color-mix(in srgb, ${brand} 55%, white)`; // readable accent on the dark hero
  return (
    <div className="min-h-screen bg-[#f7f4f0] font-body" data-brandfont data-testid="community-site" style={{ "--brand": brand }}>
      <BrandFont brand={site} />
      {/* hero */}
      <div className="relative bg-[#1a1614] text-white overflow-hidden">
        {site.has_photo && (
          <img src={`${API}/public/site/${slug}/photo`} alt={site.community}
            className="absolute inset-0 h-full w-full object-cover opacity-40" />
        )}
        <div className="relative max-w-4xl mx-auto px-6 pt-20 pb-16">
          {site.has_logo && (
            <img data-testid="site-logo" src={`${API}/public/site/${slug}/logo?v=${site.logo_ver || ""}`} alt={`${site.community} logo`}
              className="max-h-16 max-w-[240px] object-contain mb-5" />
          )}
          <div className="text-[11px] uppercase tracking-[0.25em] font-bold mb-3" style={{ color: brandLight }}>A concierge-powered community</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight" data-testid="site-community-name">{site.community}</h1>
          {site.headline && <p className="text-sand-300 text-lg mt-3 max-w-xl">{site.headline}</p>}
          {num && (
            <div className="flex gap-3 mt-7 flex-wrap">
              <a data-testid="site-text-btn" href={`sms:${num}`} style={{ backgroundColor: brand }} className="rounded-full hover:brightness-110 transition-[filter] font-bold px-6 py-3.5 flex items-center gap-2 text-white"><MessageSquareText className="h-4.5 w-4.5" /> Text the concierge</a>
              <a data-testid="site-call-btn" href={`tel:${num}`} className="rounded-full bg-white/10 hover:bg-white/20 backdrop-blur transition-colors font-bold px-6 py-3.5 flex items-center gap-2"><PhoneCall className="h-4.5 w-4.5" /> Call {num}</a>
            </div>
          )}
          <p className="text-sand-500 text-xs mt-3">Questions about the community? Text or call anytime — a real answer in seconds, 24/7.</p>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-6 py-10 space-y-8">
        {/* resident strip */}
        {/* Resident portal strip — only when the community has switched its web portal on (iter473). */}
        {site.portal_enabled && (
          <div className="rounded-2xl bg-white border border-sand-200 p-5 flex flex-wrap items-center justify-between gap-3" data-testid="site-resident-strip">
            <div>
              <div className="font-heading font-bold text-ink">Live here?</div>
              <div className="text-sm text-muted-foreground">Track requests, book amenities and vote in surveys from your resident portal.</div>
            </div>
            <div className="flex gap-2">
              <Link data-testid="site-portal-login" to={`/portal/login?from=/c/${slug}`} className="rounded-full bg-ink text-white font-semibold px-5 py-2.5 text-sm hover:bg-ink/85 transition-colors flex items-center gap-2"><LogIn className="h-4 w-4" /> Resident sign in</Link>
              <button data-testid="site-request-access" onClick={() => setRaOpen(true)} className="rounded-full border border-sand-200 bg-white font-semibold px-5 py-2.5 text-sm text-ink hover:border-ink transition-colors flex items-center gap-2"><UserPlus className="h-4 w-4" /> Request access</button>
            </div>
          </div>
        )}

        {/* board & staff have their own dashboard — discreet entry so residents aren't distracted */}
        <div className="text-center -mt-3">
          <Link data-testid="site-staff-login" to={`/c/${slug}/login`} className="text-xs font-semibold text-muted-foreground hover:text-ink inline-flex items-center gap-1.5">
            <LogIn className="h-3.5 w-3.5" /> HOA board or staff? Sign in here
          </Link>
        </div>

        {/* opt-in strip — the community's enrollment front door (QR for desktop visitors) */}
        {site.optin_slug && (
          <section data-testid="site-optin-section" className="rounded-2xl border p-5 sm:p-6 flex flex-wrap items-center gap-5"
            style={{ borderColor: `color-mix(in srgb, ${brand} 30%, white)`, backgroundColor: `color-mix(in srgb, ${brand} 6%, white)` }}>
            <div className="flex-1 min-w-[240px]">
              <div className="font-heading font-bold text-ink flex items-center gap-2"><BellRing className="h-4.5 w-4.5" style={{ color: brand }} /> Get community texts</div>
              <p className="text-sm text-muted-foreground mt-1">Opt in once and get instant answers and community alerts by text — reply STOP anytime.</p>
              <a data-testid="site-optin-btn" href={`/opt-in/${site.optin_slug}`}
                style={{ backgroundColor: brand }}
                className="inline-flex items-center gap-2 rounded-full text-white font-bold px-5 py-2.5 text-sm mt-3 hover:brightness-110 transition-[filter]">
                <MessageSquareText className="h-4 w-4" /> Opt in / Register
              </a>
            </div>
            <div className="hidden sm:flex flex-col items-center gap-1.5" data-testid="site-optin-qr">
              <div className="p-2.5 rounded-xl border border-sand-200 bg-white">
                <QRCodeCanvas value={`${window.location.origin}/opt-in/${site.optin_slug}`} size={96} level="M" fgColor="#141417" />
              </div>
              <span className="text-[10px] font-semibold text-ink/45 uppercase tracking-wide">Scan on your phone</span>
            </div>
          </section>
        )}

        {(site.welcome || site.about) && (
          <section className="grid md:grid-cols-2 gap-5">
            {site.welcome && <div className="bg-white rounded-2xl border border-sand-200 p-6"><h2 className="font-heading font-bold text-ink mb-2">Welcome</h2><p className="text-sm text-ink-700 whitespace-pre-wrap">{site.welcome}</p></div>}
            {site.about && <div className="bg-white rounded-2xl border border-sand-200 p-6"><h2 className="font-heading font-bold text-ink mb-2">About the community</h2><p className="text-sm text-ink-700 whitespace-pre-wrap">{site.about}</p></div>}
          </section>
        )}

        {site.amenities?.length > 0 && (
          <section data-testid="site-amenities">
            <h2 className="font-heading font-bold text-ink mb-3 flex items-center gap-2"><Sparkles className="h-4.5 w-4.5" style={{ color: brand }} /> Amenities</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {site.amenities.map((a, i) => (
                <div key={i} className="bg-white rounded-2xl border border-sand-200 p-4">
                  <div className="font-semibold text-ink text-sm">{a.name}</div>
                  {a.description && <p className="text-xs text-muted-foreground mt-1">{a.description}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        {site.events?.length > 0 && (
          <section data-testid="site-events">
            <h2 className="font-heading font-bold text-ink mb-3 flex items-center gap-2"><CalendarDays className="h-4.5 w-4.5" style={{ color: brand }} /> Upcoming events</h2>
            <div className="space-y-2">
              {site.events.map((e, i) => (
                <div key={i} className="bg-white rounded-2xl border border-sand-200 px-4 py-3 flex items-center justify-between gap-3">
                  <span className="font-semibold text-ink text-sm">{e.title}</span>
                  <span className="text-xs text-muted-foreground shrink-0">{fmtDate(e.date)}{e.time ? ` · ${e.time}` : ""}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {site.payments && (
          <section className="bg-white rounded-2xl border border-sand-200 p-6" data-testid="site-payments">
            <h2 className="font-heading font-bold text-ink mb-3 flex items-center gap-2"><CreditCard className="h-4.5 w-4.5" style={{ color: brand }} /> Payments &amp; dues</h2>
            <div className="grid md:grid-cols-2 gap-5">
              <div className="space-y-2 text-sm">
                {site.payments.frequency && <p className="text-ink">Assessments are payable <b className="uppercase">{site.payments.frequency}</b>.</p>}
                {site.payments.association_id && <p className="text-ink">Association ID: <b>{site.payments.association_id}</b></p>}
                {site.payments.accounting && <p className="text-ink-700">Accounting help: {site.payments.accounting}</p>}
                <p className="text-xs text-muted-foreground">Include your association name and property address on all payments.</p>
              </div>
              {(site.payments.mail_name || site.payments.mail_address) && (
                <div className="rounded-xl bg-sand-50 border border-sand-100 p-4">
                  <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1.5">Mail payments to</div>
                  <div className="text-sm text-ink whitespace-pre-wrap">{[site.payments.mail_name, site.payments.mail_address].filter(Boolean).join("\n")}</div>
                </div>
              )}
            </div>
          </section>
        )}

        {site.quick_links?.length > 0 && (
          <section data-testid="site-quick-links">
            <h2 className="font-heading font-bold text-ink mb-3 flex items-center gap-2"><Link2 className="h-4.5 w-4.5" style={{ color: brand }} /> Quick links</h2>
            <div className="grid sm:grid-cols-2 gap-2.5">
              {site.quick_links.map((l, i) => (
                <a key={i} href={l.url} target="_blank" rel="noreferrer"
                  className="bg-white rounded-2xl border border-sand-200 px-4 py-3.5 flex items-center justify-between gap-3 text-sm font-semibold text-ink hover:border-ink transition-colors">
                  {l.label} <ExternalLink className="h-3.5 w-3.5 text-sand-400 shrink-0" />
                </a>
              ))}
            </div>
          </section>
        )}

        {site.documents?.length > 0 && (
          <section data-testid="site-documents">
            <h2 className="font-heading font-bold text-ink mb-3 flex items-center gap-2"><FileText className="h-4.5 w-4.5" style={{ color: brand }} /> Community documents</h2>
            <div className="space-y-2">
              {site.documents.map((d) => (
                <a key={d.id} data-testid={`site-doc-download-${d.id}`} href={`${API}/public/site/${slug}/documents/${d.id}/download`}
                  className="bg-white rounded-2xl border border-sand-200 px-4 py-3 flex items-center justify-between gap-3 hover:border-ink transition-colors">
                  <span className="font-semibold text-ink text-sm truncate">{d.title}</span>
                  <span className="flex items-center gap-1.5 text-xs font-bold shrink-0" style={{ color: brand }}><Download className="h-3.5 w-3.5" /> Download</span>
                </a>
              ))}
            </div>
          </section>
        )}

        {(site.contact?.name || site.contact?.email || site.contact?.phone) && (
          <section className="bg-white rounded-2xl border border-sand-200 p-6" data-testid="site-contact">
            <h2 className="font-heading font-bold text-ink mb-2">Management contact</h2>
            {site.contact.name && <div className="text-sm font-semibold text-ink">{site.contact.name}</div>}
            <div className="flex flex-wrap gap-4 mt-1.5 text-sm">
              {site.contact.email && <a href={`mailto:${site.contact.email}`} className="flex items-center gap-1.5 text-ink-700 hover:text-redline-600"><Mail className="h-3.5 w-3.5" /> {site.contact.email}</a>}
              {site.contact.phone && <a href={`tel:${site.contact.phone}`} className="flex items-center gap-1.5 text-ink-700 hover:text-redline-600"><PhoneIcon className="h-3.5 w-3.5" /> {site.contact.phone}</a>}
            </div>
            <p className="text-xs text-muted-foreground mt-3">Fastest way to get help: text the concierge number above — it answers instantly and loops in the team when needed.</p>
          </section>
        )}
      </main>

      {/* lead-machine footer */}
      <footer className="bg-[#1a1614] text-white mt-6" data-testid="site-lead-footer">
        <div className="max-w-4xl mx-auto px-6 py-10 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="font-heading font-extrabold">Powered by Ask <span className="text-[#D32F2F]">Redline</span></div>
            <div className="text-sand-400 text-sm mt-1">The AI concierge your community can text or call — 24/7 answers, requests, bookings and more.</div>
            {site.mgmt?.name && (
              <div className="text-sand-500 text-xs mt-2" data-testid="site-mgmt-footer">
                Professionally managed by {site.mgmt.website
                  ? <a href={site.mgmt.website.startsWith("http") ? site.mgmt.website : `https://${site.mgmt.website}`} target="_blank" rel="noreferrer" className="underline hover:text-white">{site.mgmt.name}</a>
                  : <span className="font-semibold">{site.mgmt.name}</span>}
              </div>
            )}
          </div>
          <Link data-testid="site-lead-cta" to="/?utm_source=community_site&utm_medium=footer" className="rounded-full bg-redline-500 hover:bg-redline-600 transition-colors font-bold px-6 py-3 text-sm shrink-0">Want this for your HOA? →</Link>
        </div>
      </footer>

      <RequestAccessDialog slug={slug} community={site.community} open={raOpen} onClose={() => setRaOpen(false)} />
    </div>
  );
}

export { RequestAccessDialog };
